import React, { useState, useCallback } from 'react';
import { Icon, Modal, Alert, Button, SpaceBetween, Box } from '@amzn/awsui-components-core';
import { RecentMeeting, AttendeePriority } from '../../interfaces/interface';
import { MeetingCard } from './MeetingCard';
import styles from './Sidebar.module.scss';

export interface MyMeetingsSectionProps {
  recentMeetings: RecentMeeting[];
  totalMeetingCount: number;
  onDeleteMeeting: (meetingId: string) => Promise<void>;
  existingLoginNames: Set<string>;
  onFavoriteClick: (loginName: string, priority?: AttendeePriority) => void;
  isReadOnly: boolean;
  isMeetingRegistered: boolean;
  onNewMeeting?: () => void;
}

export const MyMeetingsSection: React.FC<MyMeetingsSectionProps> = ({
  recentMeetings,
  totalMeetingCount,
  onDeleteMeeting,
  existingLoginNames,
  onFavoriteClick,
  isReadOnly,
  isMeetingRegistered,
  onNewMeeting
}) => {
  const [pendingDeleteMeeting, setPendingDeleteMeeting] = useState<{ meetingId: string; subject: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingDeleteMeeting) return;
    setIsDeleting(true);
    try {
      await onDeleteMeeting(pendingDeleteMeeting.meetingId);
    } finally {
      setIsDeleting(false);
      setPendingDeleteMeeting(null);
    }
  }, [pendingDeleteMeeting, onDeleteMeeting]);

  return (
    <>
      <div className={styles.meetingsSection}>
        <div className={styles.meetingsSectionContent}>
          <div className={styles.meetingsSectionHeader}>
            <Icon name="calendar" size="small" />
            <p className={styles.meetingsSectionHeading}>My Meetings ({totalMeetingCount})</p>
            {onNewMeeting && (
              <button type="button" className={styles.newMeetingBtn} onClick={onNewMeeting} aria-label="Create new meeting" title="New meeting">
                <Icon name="add-plus" size="small" />
              </button>
            )}
          </div>

          {recentMeetings.length === 0 ? (
            <div className={styles.meetingsEmptyState}>
              <div className={styles.meetingsEmptyIllustration}>
                <Icon name="calendar" size="big" variant="subtle" />
              </div>
              <p className={styles.meetingsEmptyTitle}>No meetings yet</p>
              <p className={styles.meetingsEmptyText}>
                Your scheduled meetings will appear here. Start by uploading a file, cloning a past meeting, or adding attendees manually.
              </p>
            </div>
          ) : (
            <div className={styles.meetingsList}>
              {recentMeetings.map((meeting) => (
                <MeetingCard
                  key={meeting.meetingId}
                  meeting={meeting}
                  existingLoginNames={existingLoginNames}
                  onFavoriteClick={onFavoriteClick}
                  onDelete={(meetingId, subject) => setPendingDeleteMeeting({ meetingId, subject })}
                  isReadOnly={isReadOnly}
                  isMeetingRegistered={isMeetingRegistered}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {pendingDeleteMeeting && (
        <Modal
          visible={true}
          onDismiss={() => setPendingDeleteMeeting(null)}
          header="Delete Meeting"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setPendingDeleteMeeting(null)}>
                  Cancel
                </Button>
                <Button variant="normal" onClick={handleDeleteConfirm} loading={isDeleting}>
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Alert type="warning">This will remove the meeting from your My Meetings list.</Alert>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--huddle-text-body)' }}>
              Are you sure you want to delete <strong>{pendingDeleteMeeting.subject}</strong>? The meeting will be removed from your list, but the
              shared link will continue to work.
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};
